import { useCallback, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Autocomplete,
} from "@mui/material";
import { useUserContext } from "../../../contexts/ProviderProvider";
import useNotification from "../../../hooks/useNotification";
import { getProductMetadata } from "../../../API/productMetadata";
import { updateProductMetadata } from "../../../API/productMetadataUpdate";
import { getAllProductConfigurations } from "../../../API/productConfiguration";
import ServerSideTable from "../../common/ServerSideTable";
import axios from "axios";
import useCanUser from "../../../hooks/useCanUser"; // Import the useCanUser hook

const ProductMetadata = ({ pm }) => {
  const [productMetadata, setProductMetadata] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const user = useUserContext();
  const notify = useNotification();

  // Check if the user can edit product metadata
  const canEdit = useCanUser("edit", ["product_metadata"]);

  const options = {
    expandableRows: false,
    selectableRows: "none",
    sort: true,
    filter: true,
  };

  const handlePaginationStateUpdate = useCallback(
    (axiosOptions, page, pageSize, search, filter, ordering) => {
      console.log(`axiosOptions`, axiosOptions);
      console.log(`page`, page);
      console.log(`pagesize`, pageSize);
      console.log(`search`, search);
      console.log(`filter`, filter);
      console.log(`ordering`, ordering);

      return getProductMetadata(
        user,
        axiosOptions,
        page,
        pageSize,
        ordering,
        search,
        filter || {}
      )
        .then((response) => {
          const metadataArray = response.data?.obj?.productMetadata ?? [];
          setProductMetadata(metadataArray);
          return response;
        })
        .catch((e) => {
          if (!axios.isCancel(e)) {
            notify("Failed to get product metadata");
            return e;
          }
        });
    },
    [user, pm, refresh]
  );

  const handleButtonClick = (rowData, tableMeta) => {
    const rowObject = columns.reduce((acc, col, index) => {
      if (col.name !== "Action") {
        acc[col.name] = rowData[index];
      }
      return acc;
    }, {});

    setSelectedRowData(rowObject);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedRowData(null);
  };

  let columns = [];

  if (Array.isArray(productMetadata) && productMetadata.length > 0) {
    const shape = productMetadata[0] ?? {};
    columns = Object.keys(shape).map((col) => ({
      label: capitalize(col),
      name: col,
    }));
  }

  // Only add the "Action" column if the user has permission to edit
  if (canEdit) {
    columns.unshift({
      name: "Action",
      label: "Action",
      options: {
        customBodyRender: (value, tableMeta) => {
          return (
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleButtonClick(tableMeta.rowData, tableMeta)}
            >
              Edit
            </Button>
          );
        },
      },
    });
  }

  return (
    <>
      <ServerSideTable
        title={"Product Metadata"}
        data={productMetadata}
        columns={columns}
        onTableChange={() => {}}
        tableConfig={options}
        paginatedRequestFunc={handlePaginationStateUpdate}
        hideColumns={[]}
        initialSort={{ name: "product_metadata_id", direction: "ascending" }}
      />

      {isFormOpen && selectedRowData && (
        <EditForm
          open={isFormOpen}
          handleClose={handleCloseForm}
          data={selectedRowData}
          user={user}
          axiosOptions={{}}
          setRefresh={setRefresh}
          productMetadata={productMetadata}
          canEdit={canEdit} // Pass canEdit to the form
        />
      )}
    </>
  );
};

// Capitalize function
const capitalize = (key) => {
  const words = key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .split(" ");
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const EditForm = ({
  open,
  handleClose,
  data,
  user,
  axiosOptions,
  setRefresh,
  productMetadata,
  canEdit,
}) => {
  const [formData, setFormData] = useState({
    ...data,
    color_filter: "",
    filter_style: "",
    selected_product: null, // Store the selected product configuration object
  });
  const [productConfigurations, setProductConfigurations] = useState([]);
  const notify = useNotification();

  const uniqueColors = [
    ...new Set(productConfigurations.map((config) => config.color_reference)),
  ]; // Get unique colors
  const uniqueStyles = [
    ...new Set(productMetadata.map((item) => item.style).filter(Boolean)),
  ]; // Pull from table

  const selectedType = data?.type || "";
  const selectedStyle = data?.style || "";

  useEffect(() => {
    if (open && data) {
      setFormData((prev) => ({
        ...prev,
        product_type: selectedType,
        product_style: selectedStyle, // Pass `product_style` to form
      }));
    }
  }, [open, data, selectedType, selectedStyle]);

  useEffect(() => {
    const fetchProductConfigurations = async () => {
      try {
        const response = await getAllProductConfigurations(user);
        const configData = response?.data;

        if (!configData || typeof configData !== "object") {
          setProductConfigurations([]);
          console.error("Unexpected API response format:", response);
          return;
        }

        const flattenedConfigurations = Object.entries(configData).flatMap(
          ([color, types]) =>
            Object.entries(types).flatMap(([productType, configs]) =>
              configs.map((config) => ({
                public_color: color,
                product_config_id: config.product_config_id,
                product_type: productType,
                product_style: config.product_style || "",
                color_reference: config.color_reference || "",
                size_range: config.size_range || "N/A",
              }))
            )
        );

        setProductConfigurations(flattenedConfigurations);
      } catch (error) {
        setProductConfigurations([]);
        console.error("Failed to fetch product configurations:", error);
      }
    };

    if (open) fetchProductConfigurations();
  }, [open, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      // Find the selected product configuration from the dropdown
      const selectedConfig = formData.selected_product;

      if (!selectedConfig) {
        notify("Please select a valid product configuration.");
        return;
      }

      await updateProductMetadata(
        user,
        axiosOptions,
        formData.product_metadata_id,
        selectedConfig.size_range, // Use selected size range
        selectedStyle, // Use selected style
        selectedConfig.color_reference // Use selected color reference
      );

      notify("Product metadata updated successfully!");
      setRefresh((prev) => prev + 1);
      handleClose();
    } catch (error) {
      notify("Failed to update product metadata");
      console.error("Update Error:", error);
    }
  };

  const filteredConfigurations = productConfigurations.filter((config) => {
    const matchesType = selectedType && config.product_type === selectedType;
    const matchesColor =
      !formData.color_filter ||
      config.color_reference
        .toLowerCase()
        .includes(formData.color_filter.toLowerCase());
    const matchesStyle =
      !formData.filter_style ||
      config.product_style
        .toLowerCase()
        .includes(formData.filter_style.toLowerCase());
    return matchesType && matchesColor && matchesStyle;
  });

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Edit Metadata</DialogTitle>
      <DialogContent>
        <TextField
          label="Product ID"
          name="product_id"
          value={formData.product_metadata_id}
          fullWidth
          margin="dense"
          disabled
        />
        <TextField
          label="Product Type"
          name="product_type"
          value={formData.product_type}
          fullWidth
          margin="dense"
          disabled
        />
        <TextField
          label="Color Reference"
          name="color_reference"
          value={formData.color_reference}
          fullWidth
          margin="dense"
          disabled
        />
        <TextField
          label="Size Range"
          name="size_range"
          value={formData.size_range || ""}
          fullWidth
          margin="dense"
          disabled
        />

        {/* Product Configuration Autocomplete */}
        <Autocomplete
          options={filteredConfigurations}
          value={formData.selected_product}
          onChange={(event, newValue) =>
            setFormData({ ...formData, selected_product: newValue })
          }
          getOptionLabel={(option) =>
            `${option.color_reference} | ${
              option.product_type
            } | ${selectedStyle} | ${option.size_range || "N/A"}`
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select a new Product Configuration"
              fullWidth
              margin="dense"
              disabled={!canEdit} // Disable if the user cannot edit
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {option.public_color + " " + option.product_type}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {selectedStyle} | {option.color_reference} |{" "}
                  {option.size_range || "N/A"}
                </Typography>
              </div>
            </li>
          )}
          disabled={!canEdit} // Disable if the user cannot edit
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" disabled={!canEdit}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductMetadata;
